import { Geolocation, type Position } from '@capacitor/geolocation';
import { isNative } from './is-native';

export interface PickedLocation {
  lat: number;
  lng: number;
  accuracy: number | null;
  /**
   * When the phone took the reading (epoch ms). `locationIfAllowed` accepts a
   * reading up to two minutes old, so this is not always now — and the On my
   * way arrival time needs to know which.
   */
  takenAt?: number;
}

/**
 * Get current position. Native uses the Capacitor Geolocation plugin so iOS
 * gets the system permission prompt + Info.plist usage string respected;
 * web falls back to the browser navigator.geolocation API.
 *
 * Returns null if permission is denied or the user cancels.
 */
export async function pickLocation(): Promise<PickedLocation | null> {
  if (isNative()) {
    try {
      const perm = await Geolocation.requestPermissions();
      if (perm.location !== 'granted') return null;
      const position: Position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10_000,
      });
      return {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy ?? null,
      };
    } catch {
      return null;
    }
  }

  // Web fallback
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return null;
  return new Promise<PickedLocation | null>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy ?? null,
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  });
}

/**
 * Where the phone is, ONLY if the person has already said yes — never a prompt.
 *
 * For surfaces that would be nicer with a location but must not ask for one,
 * like the glass Home's "drive to the next job". A permission prompt on the
 * most-opened screen in the app, for a nicety, is the wrong trade; the screen
 * falls back to something it can say without one.
 */
export async function locationIfAllowed(): Promise<PickedLocation | null> {
  if (isNative()) {
    try {
      const perm = await Geolocation.checkPermissions();
      if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') return null;
      const position: Position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 8_000,
        maximumAge: 120_000,
      });
      return { lat: position.coords.latitude, lng: position.coords.longitude, accuracy: position.coords.accuracy ?? null, takenAt: position.timestamp };
    } catch {
      return null;
    }
  }

  if (typeof navigator === 'undefined' || !('geolocation' in navigator) || !navigator.permissions?.query) return null;
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    if (status.state !== 'granted') return null;
  } catch {
    return null;
  }
  return new Promise<PickedLocation | null>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy ?? null, takenAt: pos.timestamp }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 8_000, maximumAge: 120_000 },
    );
  });
}
